import { useState, useEffect } from 'react';
import { awardsData } from '../../../mocks/awards';

export default function Awards() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeAward, setActiveAward] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('awards');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="awards" className="relative py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-3">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-[#800000] mb-4 px-6 py-2 border border-[#800000]/20 bg-[#800000]/5" style={{ fontFamily: "'Inter', sans-serif" }}>Recognition</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-black text-center mb-4 tracking-tight" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
            Awards & Honors
          </h2>
          <p className="text-base text-center text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Recognition of excellence in academics, leadership, and community service
          </p>
        </div>

        {/* Awards Grid */}
        <div className="grid md:grid-cols-2 gap-px bg-gray-200">
          {awardsData.map((award, index) => (
            <div
              key={index}
              className={`bg-white p-10 group cursor-pointer transition-all duration-500 hover:bg-gray-50 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setActiveAward(index)}
              onMouseLeave={() => setActiveAward(null)}
            >
              <div className="flex items-start gap-6">
                {/* Award Icon */}
                <div className="flex-shrink-0">
                  <div className={`w-14 h-14 flex items-center justify-center bg-[#800000] text-white transition-all duration-300 ${activeAward === index ? 'scale-110' : 'scale-100'}`}>
                    <i className={`${award.icon} text-2xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                </div>

                {/* Award Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-black tracking-tight pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {award.title}
                    </h3>
                    <span className="text-xs font-semibold text-[#800000] uppercase tracking-wider whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {award.year}
                    </span>
                  </div>
                  
                  <p className="text-sm font-medium text-gray-600 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {award.organization}
                  </p>
                  
                  <p className="text-sm text-gray-700 leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.7' }}>
                    {award.description}
                  </p>

                  {/* Award Image */}
                  {award.image && (
                    <div className="mt-4 relative overflow-hidden h-64 bg-gray-100">
                      <img
                        src={award.image}
                        alt={award.title}
                        className={`w-full h-full object-contain object-center transition-all duration-700 ${activeAward === index ? 'scale-105' : 'scale-100'}`}
                      />
                      <div className="absolute inset-0 border border-black/5"></div>
                    </div>
                  )}

                  {/* Achievement Metrics */}
                  {award.metrics && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-4 text-xs">
                        {award.metrics.map((metric, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-[#800000]"></div>
                            <span className="text-gray-600 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{metric}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="#gallery"
            className="group inline-flex items-center gap-3 bg-[#800000] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-black transition-all duration-300 whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span>View Photo Gallery</span>
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300 w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
